
import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Plus, Edit, Trash } from "lucide-react";

interface StoryListProps {
  stories: any[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onAddStory: () => void;
  onEditStory: (story: any) => void;
  onDeleteStory: (storyId: number) => void;
}

const StoryList: React.FC<StoryListProps> = ({
  stories,
  searchTerm,
  onSearchChange,
  onAddStory,
  onEditStory,
  onDeleteStory
}) => {
  const { language } = useLanguage();
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder={language === "en" ? "Search stories..." : "البحث عن القصص..."}
            className="pl-10"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        
        <Button 
          type="button"
          onClick={onAddStory}
          className="bg-palsustain-green hover:bg-palsustain-green/90"
        >
          <Plus className="mr-2 h-4 w-4" />
          {language === "en" ? "Add Story" : "إضافة قصة"}
        </Button>
      </div>
      
      <div className="border rounded-md">
        <div className="grid grid-cols-12 gap-4 p-4 border-b bg-muted/30 font-medium text-sm">
          <div className="col-span-5">{language === "en" ? "Title" : "العنوان"}</div>
          <div className="col-span-2">{language === "en" ? "Date" : "التاريخ"}</div>
          <div className="col-span-2 text-center">{language === "en" ? "Featured" : "مميز"}</div>
          <div className="col-span-3 text-right">{language === "en" ? "Actions" : "الإجراءات"}</div>
        </div>
        
        {stories.length > 0 ? (
          stories.map((story) => (
            <div key={story.id} className="grid grid-cols-12 gap-4 p-4 border-b items-center">
              <div className="col-span-5 font-medium truncate">
                {language === "en" ? story.title_en : story.title_ar}
              </div>
              <div className="col-span-2 text-sm text-muted-foreground">
                {new Date(story.date).toLocaleDateString(language === "en" ? "en-US" : "ar-EG")}
              </div>
              <div className="col-span-2 text-center">
                {story.featured ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {language === "en" ? "Yes" : "نعم"}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {language === "en" ? "No" : "لا"}
                  </span>
                )}
              </div>
              <div className="col-span-3 flex justify-end gap-2">
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline"
                  onClick={() => onEditStory(story)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  type="button" 
                  size="sm" 
                  variant="destructive"
                  onClick={() => onDeleteStory(story.id)}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            {language === "en" 
              ? "No stories found matching your search" 
              : "لم يتم العثور على قصص تطابق بحثك"
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryList;
